import React from 'react';
import './SearchResults.css';
import PropTypes from 'prop-types';
import PropTypeShapes from '../../services/Constants/PropTypeShapes.jsx';
import { AsyncImage } from 'loadable-image';
import { Grow } from 'transitions-kit';
import { captureMessage } from '@sentry/react';

/**
 * Renders the vendor icon using AsyncImage
 * @param {object} vendor - the vendor object
 * @returns {JSX.Element} vendor icon element
 */
const getVendorIcon = vendor => (
  <AsyncImage
    className='search-result-icon'
    loader={ <div className='search-result-icon skeleton-loader' /> }
    Transition={ Grow }
    onError={
      event => {
        captureMessage('Failed to load search vendor icon', 'warning');
        event.currentTarget.src = vendor?.iconUrl;
      }
    }
    src={ vendor.iconUrl }
    alt={ vendor.name }
  />
);

/**
 * @param {object} props - the props of the component
 * @param {Array} props.vendors - list of matching vendors
 * @param {Function} props.onVendorSelect - callback when a vendor is selected
 * @returns {JSX.Element} SearchResults Component
 */
export default function SearchResults({ vendors, onVendorSelect }) {
  if (!vendors || vendors.length === 0) {
    return (
      <section className='search-results-empty'>
        <p className='search-results-empty-title'>We couldn&apos;t find that store</p>
        <p className='search-results-empty-subtitle'>
          Try checking your spelling or searching with a
          different name.
        </p>
      </section>
    );
  }

  const resultText = vendors.length === 1
    ? '1 result found'
    : `${vendors.length} results found`;

  return (
    <section className='search-results'>
      <p className='search-results-count'>{ resultText }</p>
      <div className='search-results-grid'>
        {
          vendors.map(vendor => (
            <button
              key={ vendor.id }
              type='button'
              className='search-results-item'
              onClick={ () => onVendorSelect?.(vendor) }
            >
              { getVendorIcon(vendor) }
              <span className='search-results-item-name'>{ vendor.name }</span>
            </button>
          ))
        }
      </div>
    </section>
  );
}

SearchResults.propTypes = {
  vendors: PropTypes.arrayOf(PropTypeShapes.vendor),
  onVendorSelect: PropTypes.func,
};
